import { useTranslation } from 'react-i18next'

import { QRCodeCanvas } from 'qrcode.react'

import { tVar } from '@/utils/i18n'

import { CheckpointIProps } from '../../types/types'
import { PRINT_CONFIG, QR_SIZE_RATIO, splitIntoPages } from '../utils/bulkPrint'

interface BulkPrintIProps {
  checkpoints: CheckpointIProps[]
}

/**
 * PagePreview shows a scaled-down simulation of the printed page
 * with proper US Letter proportions and QR code placement.
 *
 * The paper, its border and the QR codes stay fixed light-on-white in both
 * colour schemes — it is a preview of physical paper, not a themed surface.
 */
export const PagePreview = ({ checkpoints }: BulkPrintIProps) => {
  const { t } = useTranslation()
  const preview = PRINT_CONFIG.preview
  const pages = splitIntoPages(checkpoints)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {pages.map((pageCheckpoints, pageIndex) => (
        <div key={pageIndex} style={{ margin: '0 auto' }}>
          {/* Page number indicator - outside the paper */}
          <div
            style={{
              fontSize: '11px',
              color: '#666',
              textAlign: 'center',
              marginBottom: '4px',
            }}
          >
            {tVar(t, 'floors:print_page_indicator', {
              current: pageIndex + 1,
              total: pages.length,
            })}
          </div>

          {/* Paper simulation */}
          <div
            style={{
              width: preview.sheetWidth,
              height: preview.sheetHeight,
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              borderRadius: '2px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              position: 'relative',
            }}
          >
            {/* QR Grid */}
            <div
              style={{
                position: 'absolute',
                top: preview.marginTop,
                left: preview.marginLeft,
                display: 'grid',
                gridTemplateColumns: `repeat(${PRINT_CONFIG.columns}, ${preview.cellSize}px)`,
                gridAutoRows: `${preview.cellSize}px`,
                gap: 0,
              }}
            >
              {pageCheckpoints.map((checkpoint) => (
                <div
                  key={checkpoint.id}
                  style={{
                    width: preview.cellSize,
                    height: preview.cellSize,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <QRCodeCanvas
                    level='H'
                    size={Math.floor(preview.cellSize * QR_SIZE_RATIO)}
                    value={checkpoint.id}
                  />
                  <span
                    style={{
                      fontSize: '5px',
                      fontWeight: 500,
                      color: '#000',
                      lineHeight: 1,
                    }}
                  >
                    {checkpoint.displayId}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * PrintContent uses raw HTML elements (<div>) instead of Chakra components
 * because this content is cloned and injected into a new print window.
 * Chakra components rely on CSS-in-JS which won't be available in the print context.
 *
 * CSS classes in the print window handle sizing at 96 DPI (1 inch = 96px).
 */
export const PrintContent = ({ checkpoints }: BulkPrintIProps) => {
  // Render QR smaller than cell to fit label below
  const qrCanvasSize = Math.floor(PRINT_CONFIG.print.qrSize * QR_SIZE_RATIO)
  const pages = splitIntoPages(checkpoints)

  // For print, render with exact positioning and pagination
  return (
    <>
      {pages.map((pageCheckpoints, pageIndex) => (
        <div className='print-page' key={pageIndex}>
          <div className='qr-grid'>
            {pageCheckpoints.map((checkpoint) => (
              <div className='qr-cell' key={checkpoint.id}>
                <QRCodeCanvas
                  level='H'
                  size={qrCanvasSize}
                  value={checkpoint.id}
                />
                <span className='qr-label'>{checkpoint.displayId}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}
